/**
 * Job Categories API Contract
 *
 * ts-rest contract definition for the job categories endpoint used by works-lite.
 * This contract ensures type safety between frontend and backend.
 *
 * Endpoint  : GET /jobs/job-categories
 * Method    : GET
 * Auth      : Required (standard bearer token via middleware)
 * Response  : Array of active job categories ordered by name
 *
 * Backend SQL:
 *   SELECT * FROM skewb_works.job_category
 *   WHERE is_delete = false
 *   ORDER BY name;
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

/**
 * Zod schema for a single job category record
 */
export const jobCategorySchema = z.object({
  id: z.number(),
  name: z.string(),
  code: z.string().nullable(),
  type: z.string(),
  job_audit_form_id: z.number().nullable(),
  time: z.number().nullable(),
  is_system: z.boolean().nullable(),
  import_name: z.string().nullable(),
  is_delete: z.boolean().nullable(),
  created_by: z.string().nullable(),
  created_time: z.string().nullable(),
  updated_by: z.string().nullable(),
  updated_time: z.string().nullable(),
  is_runtime_task: z.boolean().nullable(),
  can_abort: z.boolean().nullable(),
});

/**
 * Zod schema for the job categories list response
 */
export const jobCategoriesResponseSchema = z.array(jobCategorySchema);

/**
 * TypeScript types inferred from Zod schemas
 */
export type JobCategory = z.infer<typeof jobCategorySchema>;
export type JobCategoriesResponse = z.infer<typeof jobCategoriesResponseSchema>;

/**
 * Job Categories API Contract
 *
 * Base path: /jobs/job-categories  (mounted in apps/backend/src/routes/index.ts)
 */
export const jobCategoriesContract = c.router({
  /**
   * Get all active job categories
   *
   * GET /jobs/job-categories
   *
   * Returns every job_category row where is_delete = false,
   * sorted alphabetically by name. No query parameters required.
   *
   * Used by: works-lite job type filter dropdown and analytics filters.
   */
  getJobCategories: {
    method: 'GET',
    path: '/',
    query: z.object({
      withCode: z.enum(['true']).optional(),
    }),
    responses: {
      200: jobCategoriesResponseSchema,
      500: z.object({
        error: z.object({
          message: z.string(),
          code: z.string().optional(),
        }),
      }),
    },
    summary: 'Get all active job categories ordered by name',
    description:
      'Returns all job categories where is_delete = false, ordered alphabetically by name.',
  },
});
